// src/Views/LogInOrSignUpView.js
import React, { useState, useRef, useEffect } from 'react';
import { Link } from 'react-router-dom';
import EnterPasswordView from './EnterPasswordView';
import CreateAccountView from './CreateAccountView';
import { BORDER_RADIUS, SPACING_ELEMENT, SPACING_COMPONENT, SPACING_BUTTON, SPACING_CARD, SPACING_CARD_BODY, SPACING_GROUP, SPACING_SECTION } from '../constants/layout';
import { FONT_TITLE2, FONT_BODY } from '../constants/typography';
import { AUTH_ACTION_LABEL } from '../constants/settings';

function LogInOrSignUpView({ onClose, onGoogleSignInRequested }) {
  const [email, setEmail] = useState('');
  const [errorMessage, setErrorMessage] = useState(null);
  const [showError, setShowError] = useState(false);
  const [destination, setDestination] = useState(null); // 'signIn' or 'createAccount'
  const emailInputRef = useRef(null);

  useEffect(() => {
    // Auto-focus email input
    emailInputRef.current?.focus();
  }, []);

  const trimmedEmail = email.trim();

  const isValidEmail = (email) => {
    const re = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return re.test(email);
  };

  const chooseDestination = (nextDestination) => {
    if (!isValidEmail(trimmedEmail)) {
      setErrorMessage('Please enter a valid email address');
      setShowError(true);
      return;
    }
    setErrorMessage(null);
    setDestination(nextDestination);
  };

  // If navigated to password entry or account creation
  if (destination === 'signIn') {
    return <EnterPasswordView email={trimmedEmail} onBack={() => setDestination(null)} onClose={onClose} />;
  }

  if (destination === 'createAccount') {
    return <CreateAccountView email={trimmedEmail} onBack={() => setDestination(null)} onClose={onClose} />;
  }


  return (
    <>
      <div className="modal-overlay" onClick={onClose}>
        <div className="modal-container" onClick={(e) => e.stopPropagation()}>
          {/* Close button */}
          <button className="close-button" onClick={onClose} aria-label="Close">
            <span className="material-icons">close</span>
          </button>

          <div className="modal-content">
            {/* App Icon Placeholder */}
            <div className="app-icon">
              <span className="material-icons">storefront</span>
            </div>

            {/* Title */}
            <h2 className="modal-title">{AUTH_ACTION_LABEL}</h2>

            {/* Subtitle */}
            <p className="modal-subtitle">
              Save your bookmarks and access your cart from any device
            </p>

            {/* Email Input */}
            <input
              ref={emailInputRef}
              type="email"
              placeholder="Email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter') {
                  e.preventDefault();
                  chooseDestination('signIn');
                }
              }}
              className="email-input"
              autoComplete="email"
            />

            <div className="auth-action-buttons">
              <button
                onClick={() => chooseDestination('signIn')}
                disabled={!email}
                className="continue-button"
              >
                Sign In
              </button>
              <button
                onClick={() => chooseDestination('createAccount')}
                disabled={!email}
                className="create-account-button"
              >
                Create Account
              </button>
            </div>

            <p className="terms-notice">
              By continuing, you agree to Koral Krush's{' '}
              <Link to="/terms" target="_blank" rel="noopener noreferrer" className="terms-link">Terms of Service</Link>
              {' '}and{' '}
              <Link to="/privacy" target="_blank" rel="noopener noreferrer" className="terms-link">Privacy Policy</Link>.
            </p>

            {/* Divider */}
            {/* Google & Apple sign-in hidden for MVP
            <div className="divider">
              <span className="divider-line"></span>
              <span className="divider-text">OR</span>
              <span className="divider-line"></span>
            </div>

            <button
              onClick={() => {
                onClose();
                setTimeout(() => onGoogleSignInRequested(), 300);
              }}
              className="oauth-button google-oauth"
            >
              <img
                src="/google-icon.svg"
                alt="Google"
                className="oauth-icon"
              />
              <span>Continue with Google</span>
            </button>

            <button
              onClick={() => {
                console.log('Apple Sign In not yet implemented');
              }}
              className="oauth-button apple-oauth"
            >
              <img
                src="/apple-icon.svg"
                alt="Apple"
                className="oauth-icon"
              />
              <span>Continue with Apple</span>
            </button>
            */}
          </div>

          {/* Error Modal */}
          {showError && (
            <div className="error-overlay" onClick={() => setShowError(false)}>
              <div className="error-modal" onClick={(e) => e.stopPropagation()}>
                <h3>Error</h3>
                <p>{errorMessage}</p>
                <button onClick={() => setShowError(false)} className="error-button">
                  OK
                </button>
              </div>
            </div>
          )}
        </div>
      </div>

      <style jsx>{`
        .modal-overlay {
          position: fixed;
          top: 0;
          left: 0;
          right: 0;
          bottom: 0;
          background-color: rgba(0, 0, 0, 0.5);
          display: flex;
          align-items: center;
          justify-content: center;
          z-index: 1000;
          padding: ${SPACING_BUTTON};
        }

        .modal-container {
          background-color: var(--color-card-background);
          border-radius: ${BORDER_RADIUS};
          max-width: 500px;
          width: 100%;
          max-height: 90vh;
          overflow-y: auto;
          position: relative;
        }

        .close-button {
          position: absolute;
          top: ${SPACING_BUTTON};
          right: ${SPACING_BUTTON};
          background: none;
          border: none;
          cursor: pointer;
          color: var(--color-text-secondary);
          padding: ${SPACING_ELEMENT};
          display: flex;
          align-items: center;
          justify-content: center;
          border-radius: 50%;
          transition: background-color 0.2s;
        }

        .close-button:hover {
          background-color: var(--color-surface-muted);
        }

        .modal-content {
          padding: ${SPACING_SECTION} ${SPACING_SECTION};
          display: flex;
          flex-direction: column;
          gap: ${SPACING_CARD};
        }

        .app-icon {
          width: 85px;
          height: 85px;
          margin: 0 auto;
          display: flex;
          align-items: center;
          justify-content: center;
          background-color: var(--color-accent);
          border-radius: 20px;
        }

        .app-icon .material-icons {
          font-size: 48px;
          color: white;
        }

        .modal-title {
          font-size: ${FONT_TITLE2};
          font-weight: 600;
          text-align: center;
          margin: 0;
          color: var(--color-text-primary);
        }

        .modal-subtitle {
          font-size: ${FONT_BODY};
          text-align: center;
          color: var(--color-text-secondary);
          margin: 0;
          line-height: 1.5;
        }

        .email-input {
          width: 100%;
          padding: ${SPACING_BUTTON};
          border: 1px solid var(--color-border);
          border-radius: ${BORDER_RADIUS};
          font-size: 1rem;
          background-color: var(--color-surface-muted);
          color: var(--color-text-primary);
          box-sizing: border-box;
        }

        .email-input:focus {
          outline: none;
          border-color: var(--color-accent);
        }

        .continue-button {
          width: 100%;
          height: 54px;
          background-color: var(--color-accent);
          color: var(--color-accent-text);
          border: none;
          border-radius: ${BORDER_RADIUS};
          font-size: 1rem;
          font-weight: 600;
          cursor: pointer;
          transition: all 0.2s ease;
          display: flex;
          align-items: center;
          justify-content: center;
        }

        .continue-button:hover:not(:disabled) {
          background-color: var(--color-accent-hover);
          transform: translateY(-1px);
        }

        .continue-button:disabled {
          opacity: 0.5;
          cursor: not-allowed;
        }

        .auth-action-buttons {
          display: flex;
          flex-direction: column;
          gap: ${SPACING_ELEMENT};
        }

        .create-account-button {
          width: 100%;
          height: 54px;
          background: transparent;
          color: var(--color-accent);
          border: 1px solid var(--color-accent);
          border-radius: ${BORDER_RADIUS};
          font-size: 1rem;
          font-weight: 600;
          cursor: pointer;
        }

        .create-account-button:disabled {
          opacity: 0.5;
          cursor: not-allowed;
        }

        .terms-notice {
          font-size: 0.8125rem;
          color: var(--color-text-secondary);
          text-align: center;
          margin: 0;
          line-height: 1.5;
        }

        .terms-link {
          color: var(--color-accent);
          text-decoration: none;
        }

        .terms-link:hover {
          text-decoration: underline;
        }

        .button-loader {
          width: 20px;
          height: 20px;
          border: 2px solid rgba(255, 255, 255, 0.3);
          border-top-color: white;
          border-radius: 50%;
          animation: spin 0.6s linear infinite;
        }

        @keyframes spin {
          to {
            transform: rotate(360deg);
          }
        }

        .divider {
          display: flex;
          align-items: center;
          gap: ${SPACING_COMPONENT};
          margin: ${SPACING_ELEMENT} 0;
        }

        .divider-line {
          flex: 1;
          height: 1px;
          background-color: var(--color-border);
        }

        .divider-text {
          font-size: 0.875rem;
          color: var(--color-text-secondary);
        }

        .oauth-button {
          width: 100%;
          height: 54px;
          border: 1px solid var(--color-border);
          border-radius: ${BORDER_RADIUS};
          font-size: 1rem;
          font-weight: 600;
          cursor: pointer;
          transition: all 0.2s ease;
          display: flex;
          align-items: center;
          justify-content: center;
          gap: ${SPACING_COMPONENT};
        }

        .google-oauth {
          background-color: #ffffff;
          color: #3c4043;
          border: 1px solid #dadce0;
        }

        .google-oauth:hover {
          background-color: #f8f9fa;
          transform: translateY(-1px);
          box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
        }

        .apple-oauth {
          background-color: #ffffff;
          color: #000000;
          border: 1px solid #dadce0;
        }

        .apple-oauth:hover {
          background-color: #f8f9fa;
          transform: translateY(-1px);
          box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
        }

        .oauth-icon {
          width: 20px;
          height: 20px;
        }

        .google-oauth .oauth-icon {
          width: 18px;
          height: 18px;
        }

        /* Error Modal */
        .error-overlay {
          position: fixed;
          top: 0;
          left: 0;
          right: 0;
          bottom: 0;
          background-color: rgba(0, 0, 0, 0.5);
          display: flex;
          align-items: center;
          justify-content: center;
          z-index: 1001;
        }

        .error-modal {
          background-color: var(--color-card-background);
          border-radius: ${BORDER_RADIUS};
          padding: ${SPACING_CARD_BODY};
          max-width: 400px;
          width: 90%;
          text-align: center;
        }

        .error-modal h3 {
          margin: 0 0 ${SPACING_COMPONENT} 0;
          color: var(--color-text-primary);
        }

        .error-modal p {
          margin: 0 0 ${SPACING_CARD_BODY} 0;
          color: var(--color-text-secondary);
        }

        .error-button {
          padding: ${SPACING_COMPONENT} ${SPACING_CARD_BODY};
          background-color: var(--color-accent);
          color: var(--color-accent-text);
          border: none;
          border-radius: ${BORDER_RADIUS};
          font-weight: 600;
          cursor: pointer;
        }

        .error-button:hover {
          background-color: var(--color-accent-hover);
        }

        @media (max-width: 600px) {
          .modal-content {
            padding: ${SPACING_GROUP} ${SPACING_CARD_BODY};
          }
        }
      `}</style>
    </>
  );
}

export default LogInOrSignUpView;
